import { useCallback, useEffect, useRef } from "react";
import { useGetChat } from "~/api/chat/chat";
import { useGetCustomReport } from "~/api/custom-reports/custom-reports";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { useReportStore } from "../../stores/reportStore";
import { useTermsStore } from "../../stores/termsStore";
import { useTemplateReports, type TemplateReport } from "./useTemplateReports";

export type { TemplateReport } from "./useTemplateReports";

const getTemplateReportId = (templateReport: TemplateReport): string | null =>
  templateReport.report?.id ?? templateReport.template?.id ?? null;

const matchesReportId = (
  templateReport: TemplateReport,
  reportId: string
): boolean =>
  templateReport.report?.id === reportId ||
  templateReport.template?.id === reportId;

export const findReportIndexById = (
  templateReports: TemplateReport[],
  reportId: string
): number =>
  templateReports.findIndex((templateReport) =>
    matchesReportId(templateReport, reportId)
  );

export const useReportData = (
  organizationId: string,
  projectId: string,
  chatId: string
) => {
  const selectedReportIndex = useReportStore((s) =>
    s.getSelectedReportIndex(chatId)
  );
  const setSelectedReportIndexInStore = useReportStore(
    (s) => s.setSelectedReportIndex
  );
  const [{ reportId: reportIdParam }, updateDocumentParams] =
    useDocumentSearchParams();
  const lastWrittenReportIdRef = useRef<string | null | undefined>(undefined);

  const {
    templateReports,
    locallyGenerating,
    handleGenerateReport,
    isTemplatesLoading,
    isReportsLoading,
  } = useTemplateReports(organizationId, projectId);

  useEffect(() => {
    if (isTemplatesLoading || isReportsLoading) return;
    const lastWrittenReportId = lastWrittenReportIdRef.current;

    if (lastWrittenReportId !== undefined) {
      if (lastWrittenReportId === reportIdParam) {
        lastWrittenReportIdRef.current = undefined;
      } else {
        return;
      }
    }

    const matchingIndex = reportIdParam
      ? findReportIndexById(templateReports, reportIdParam)
      : -1;

    if (matchingIndex >= 0 && matchingIndex !== selectedReportIndex) {
      setSelectedReportIndexInStore(chatId, matchingIndex);
    } else if (!reportIdParam && selectedReportIndex !== 0) {
      setSelectedReportIndexInStore(chatId, 0);
    } else if (reportIdParam && matchingIndex < 0) {
      updateDocumentParams({ reportId: null });
    }
  }, [
    chatId,
    isReportsLoading,
    isTemplatesLoading,
    reportIdParam,
    selectedReportIndex,
    setSelectedReportIndexInStore,
    templateReports,
    updateDocumentParams,
  ]);

  const setSelectedReportIndex = useCallback(
    (index: number) => {
      const selectedTemplateReport = templateReports[index];
      lastWrittenReportIdRef.current = selectedTemplateReport
        ? getTemplateReportId(selectedTemplateReport)
        : null;
      setSelectedReportIndexInStore(chatId, index);
      updateDocumentParams({
        reportId: selectedTemplateReport
          ? getTemplateReportId(selectedTemplateReport)
          : null,
      });
    },
    [
      chatId,
      setSelectedReportIndexInStore,
      templateReports,
      updateDocumentParams,
    ]
  );

  const { data: chat } = useGetChat(organizationId, projectId, chatId, {
    query: { staleTime: Infinity },
  });

  const selectedReport =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].report
      : null;

  const reportId = selectedReport?.id ?? "";

  const { data: reportData, isLoading: isReportContentLoading } =
    useGetCustomReport(organizationId, projectId, reportId, {
      query: {
        enabled: !!reportId,
        refetchInterval: (query) => {
          if (!query.state.data) {
            return false;
          }

          const sections = query.state.data.reportSections ?? [];
          const needsPolling = sections.some(
            (section) =>
              section.status !== "completed" && section.status !== "failed"
          );

          return needsPolling ? 3000 : false;
        },
        refetchIntervalInBackground: true,
      },
    });

  const addTerms = useTermsStore((s) => s.addTerms);
  useEffect(() => {
    if (!reportData) return;

    const hint = reportData.hint;
    if (!hint || hint.length === 0) return;

    addTerms(projectId, hint);
  }, [reportData, projectId, addTerms]);

  const content = reportData?.text ?? "";

  return {
    content,
    locallyGenerating,
    handleGenerateReport,
    templateReports,
    isLoading:
      isTemplatesLoading ||
      isReportsLoading ||
      (!!reportId && isReportContentLoading),
    selectedReportIndex,
    setSelectedReportIndex,
    chatTitle: chat?.title ?? "",
    reportId,
    reportSections: reportData?.reportSections,
    isSourcesAddedToRaw: reportData?.isSourcesAddedToRaw ?? false,
  };
};
